import Button from "@lhd/components/Button";
import { SERVICES } from "@lhd/constants";
import axiosInstance from "@lhd/libs/axios";
import { IOptions, Tree } from "@lhd/types";
import { getCompanyId } from "@lhd/utility";
import { Form, Modal, Select, Space } from "antd";
import { debounce, flatMapDeep, pick } from "lodash";
import { useCallback, useMemo, useRef, useState, FC, useEffect } from "react";
import { RiOrganizationChart } from "react-icons/ri";
import BranchList from "./BranchList";
import { ModaBody, SearchInput } from "./styles";
import OrganizationList from "./OrganizationList";
import { AiOutlineClose } from "react-icons/ai";
import styled from "styled-components";
import { FormInstance } from "antd/es/form/Form";
import { ModelNameType } from "..";
import { useFetchOrgs } from "@lhd/hooks";
import { IOrgChartNodes } from "@lhd/features/OrganizationChart/OrganizationChartControl/types";
import axios from "@lhd/libs/axios";
import axiosV2 from "@lhd/libs/axiosV2";
import { getOrgsData, getWorkers } from "@lhd/features/Apps/utils";

export const SelectedOrganizationList = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 5px 0px;
  border-bottom: 1px solid #ebebeb;
  &:hover {
    background-color: #f5f5f5;
  }
  .o-name {
    display: flex;
    svg {
      margin-right: 5px;
    }
  }
  .o-close {
    cursor: pointer;
  }
`;

export type QueryParams = {
  branch_id?: number;
  nameOnly?: boolean;
  name_cont?: string;
};

type PropsType = {
  modelName: ModelNameType;
  watchName: any;
  form: FormInstance;
};

const OrganizationSelect: FC<PropsType> = ({ form, modelName, watchName }) => {
  const [open, setOpen] = useState(false);
  const [trees, setTrees] = useState<Tree[]>([]);
  const [selectedOrgs, setSelectedOrgs] = useState<Tree[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<Tree>();
  const fetchRef = useRef(0);
  const isWorkerSelection = modelName === "worker";
  const { data: listOrg } = useFetchOrgs();
  const [selectionOptions, setSelectionOptions] = useState<IOptions[]>([]);

  const fetchOrganizations = useCallback(
    (params: QueryParams, callback?: (branchs: Tree[]) => void) => {
      const { branch_id, nameOnly, name_cont } = params;
      axiosInstance({
        method: "get",
        url: isWorkerSelection
          ? `${SERVICES.WORKERS}/tree`
          : `${SERVICES.COMPANIES}/${getCompanyId()}/tree`,
        params: isWorkerSelection
          ? {
              page: 1,
              per_page: 1000000,
              offset: 0,
              name_cont,
            }
          : {
              model_name: modelName,
              name_cont,
              ...(branch_id && { branch_id, with_base: true }),
              ...(nameOnly && { name_only: true }),
            },
      }).then((res) => {
        if (isWorkerSelection) {
          const { branchs, departments, divisions, organizations } = res.data;
          const tree = [
            ...branchs,
            ...departments,
            ...divisions,
            ...organizations,
          ]
            .filter((item) => item.workers.length)
            .map((item) => {
              return {
                ...item,
                children: item.workers,
              };
            });
          callback ? callback(tree) : setTrees((tree as Tree[]) || []);
        } else {
          callback ? callback(res.data) : setTrees((res.data as Tree[]) || []);
        }
      });
    },
    [modelName, isWorkerSelection],
  );

  const treeOptions = useMemo(() => {
    return trees.map((tree) => ({ label: tree.name, value: tree.id }));
  }, [trees]);

  const debounceFetcher = useMemo(() => {
    const loadOptions = (name_cont: string) => {
      fetchRef.current += 1;
      fetchOrganizations({ name_cont });
    };
    return debounce(loadOptions, 500);
  }, [fetchOrganizations]);

  const selectedOrganizations: Tree[] = Form.useWatch(
    ["item_config_data", watchName],
    form,
  );

  const setOrganizations = useCallback(
    (organizations) => {
      form.setFieldValue(["item_config_data", watchName], organizations);
      form.setFieldValue(
        ["item_config_data", "h"],
        Math.max.apply(null, [organizations.length + 2, 2]),
      );
    },
    [form, watchName],
  );

  const onAdd = useCallback(() => {
    setOrganizations(
      (selectedOrganizations || []).concat(
        selectedOrgs.map((o) => ({
          id: o.id,
          name: o.name || o?.person?.name,
        })) as Tree[],
      ),
    );
    setOpen(false);
  }, [selectedOrgs, setOrganizations, selectedOrganizations]);

  const onSelectTree = useCallback(
    (selectedId: number) => {
      const selectedTree = trees.find((tree) => tree.id === selectedId);
      const isExisted = selectedOrganizations?.find((o) => o.id === selectedId);
      if (selectedTree && !isExisted) {
        const selectedTrees = (selectedOrganizations || []).concat([
          selectedTree,
        ]);
        setOrganizations(selectedTrees);
      }
    },
    [trees, selectedOrganizations, setOrganizations],
  );

  const removeOrganizations = useCallback(
    (id) => {
      const organizations = selectedOrganizations.filter((o) => o.id !== id);
      form.setFieldValue(["item_config_data", watchName], organizations);
      form.setFieldValue(
        ["item_config_data", "h"],
        Math.max.apply(null, [organizations.length + 2, 2]),
      );
    },
    [form, selectedOrganizations, watchName],
  );

  const titleModal = useMemo(() => {
    switch (modelName) {
      case "organization":
        return "組織選択";
      case "division":
        return "部署選択";
      case "branch":
        return "支店選択";
      case "department":
        return "部門選択";
      case "worker":
        return "社員選択";
      default:
        `Select ${modelName}`;
    }
  }, [modelName]);

  const flattenDeepArray: any = useCallback((originalArray: any) => {
    return flatMapDeep(originalArray, (item) => {
      const transformedItem = pick(item, ["id", "name"]);
      const children = item.children || [];

      return [transformedItem, ...flattenDeepArray(children)];
    });
  }, []);

  const getDataByOrgType = useCallback(
    async (type: string) => {
      const listId =
        listOrg?.data
          ?.filter((org) => org.org_type === type)
          ?.map((orgItem) => orgItem?.id) ?? [];
      const arrData = await Promise.all(
        listId.map(async (id) => await getOrgsData(id)),
      );

      const mergedArray = arrData.reduce(
        (prevArr, arrItem) => [...prevArr, ...arrItem],
        [],
      );

      return flattenDeepArray(mergedArray);
    },
    [flattenDeepArray, listOrg?.data],
  );

  const getSelectionOptions = useCallback(
    async (modelName: string) => {
      const data =
        modelName === "worker"
          ? await getWorkers()
          : await getDataByOrgType(
              modelName === "organization" ? "chairman" : modelName,
            );

      if (!data || data.length === 0) return;
      const convertValueToOptions = data.map((orgItem: IOrgChartNodes) => ({
        label: orgItem?.name || orgItem?.full_name,
        value: orgItem?.id,
      }));

      setSelectionOptions(convertValueToOptions);
    },
    [getDataByOrgType],
  );

  useEffect(() => {
    getSelectionOptions(modelName);
  }, [modelName, getSelectionOptions]);
  return (
    <>
      <Modal
        width={800}
        title={`${titleModal}`}
        open={open}
        onCancel={() => setOpen(false)}
        footer={
          <Space direction="horizontal">
            <Button onClick={() => setOpen(false)} danger label="いいえ" />
            <Button label={`追加 (${selectedOrgs.length})`} onClick={onAdd} />
          </Space>
        }
        bodyStyle={{
          padding: 0,
          minHeight: "60vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <ModaBody>
          <BranchList
            modelName={modelName}
            selectedBranch={selectedBranch}
            setSelectedBranch={setSelectedBranch}
          />
          <OrganizationList
            selectedBranch={selectedBranch}
            selectedOrgs={selectedOrgs}
            setSelectedOrgs={setSelectedOrgs}
          />
        </ModaBody>
      </Modal>
      <SearchInput>
        <Select
          onChange={onSelectTree}
          filterOption={false}
          options={selectionOptions}
          showSearch
          size="large"
          value={null}
          onSearch={debounceFetcher}
        />
        <div className="branch-icon" onClick={() => setOpen(true)}>
          <RiOrganizationChart size={20} color="#fff" />
        </div>
      </SearchInput>
      <Form.Item hidden name={["item_config_data", watchName]} />
      <Form.Item hidden name={["item_config_data", "h"]} />
      <div>
        {selectedOrganizations?.map((o) => (
          <SelectedOrganizationList key={o.id}>
            <div className="o-name">
              <RiOrganizationChart size={20} color="#174a83" />
              <div>{o.name || o?.person?.name}</div>
            </div>
            <AiOutlineClose
              className="o-close"
              onClick={() => removeOrganizations(o.id)}
            />
          </SelectedOrganizationList>
        ))}
      </div>
    </>
  );
};

export default OrganizationSelect;
